// Environment helpers. All VITE_-prefixed vars are bundled at build time.
//
// We centralize access here so that:
//   1. Type errors catch typos (vs. `import.meta.env.VITE_TYPO` silently
//      returning undefined).
//   2. Missing required values blow up early with a clear message instead of
//      later inside Firebase init with a confusing "API key invalid".
//
// Local-first mode. `VITE_AUTH_DISABLED=true` runs the UI against
// `roast serve` (roastmcp/serve.py), a local REST facade with auth off.
// In that mode the Firebase vars are optional and never read; the app
// sends a placeholder bearer token the local server ignores. Never set it
// on a build that talks to the cloud service — that server enforces the
// Firebase UID and would reject every call.

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `missing env var ${name} — copy web/.env.example to web/.env.local and fill it in`,
    );
  }
  return value;
}

function flag(value: string | undefined): boolean {
  return value === "true" || value === "1";
}

const authDisabled = flag(import.meta.env.VITE_AUTH_DISABLED);

function firebaseVar(name: string, value: string | undefined): string {
  return authDisabled ? (value ?? "") : required(name, value);
}

export const env = {
  authDisabled,
  firebase: {
    apiKey: firebaseVar("VITE_FIREBASE_API_KEY", import.meta.env.VITE_FIREBASE_API_KEY),
    authDomain: firebaseVar(
      "VITE_FIREBASE_AUTH_DOMAIN",
      import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    ),
    projectId: firebaseVar(
      "VITE_FIREBASE_PROJECT_ID",
      import.meta.env.VITE_FIREBASE_PROJECT_ID,
    ),
    appId: firebaseVar("VITE_FIREBASE_APP_ID", import.meta.env.VITE_FIREBASE_APP_ID),
  },
  apiBaseUrl: required("VITE_API_BASE_URL", import.meta.env.VITE_API_BASE_URL).replace(
    /\/$/,
    "",
  ),
  allowedUid: import.meta.env.VITE_ALLOWED_FIREBASE_UID ?? "",
};

export type Env = typeof env;
